import styled from "styled-components";
import Search from "shared/components/Search";
import useLessonListView from "features/teacher/hooks/lesson/useLessonListView";
import Refresh from "shared/components/Refresh";
import { Button, ButtonDisabled } from "shared/components/Button";
import { useLocation, useNavigate } from "react-router-dom";
import { hw, scrollbar } from "features/homework/homeworkUi";

interface ListViewProps {
    clickedItem: string;
    setClickedItem: React.Dispatch<React.SetStateAction<string>>;
}

const LessonListView = ({ clickedItem, setClickedItem }: ListViewProps) => {
    const { search, searchedLists, onChange, onRefreshClick } = useLessonListView();
    const location = useLocation();
    const isCounsel = location.pathname.includes("/counsel");

    return (
        <Container>
            <Header>
                <div>
                    <Title>학급 목록</Title>
                    <Sub>{isCounsel ? "상담할 학급을 선택하세요." : "학급을 선택해 정보를 확인하세요."}</Sub>
                </div>
                <Refresh onRefreshClick={onRefreshClick} />
            </Header>
            <Search placeholder="학급을 입력해 주세요" value={search} onChange={onChange} />
            <List>
                {searchedLists.length === 0 ? (
                    <Empty>
                        <EmptyTitle>{search ? "검색 결과가 없습니다" : "학급이 없습니다"}</EmptyTitle>
                        <EmptyText>
                            {search ? "다른 이름으로 다시 검색해 보세요." : "학급을 먼저 생성해 주세요."}
                        </EmptyText>
                    </Empty>
                ) : (
                    searchedLists.map((item) => (
                        <Item
                            key={item.id}
                            $on={item.id === clickedItem}
                            onClick={() => setClickedItem(item.id)}
                        >
                            {item.name}
                        </Item>
                    ))
                )}
            </List>
            {isCounsel ? null : <Footer clickedItem={clickedItem} />}
        </Container>
    );
};

type FooterProps = {
    clickedItem: string;
};

const Footer = ({ clickedItem }: FooterProps) => {
    const navigate = useNavigate();
    return (
        <Buttons>
            {clickedItem ? (
                <ButtonDisabled name="수정하기" onButtonClick={() => navigate("register", { state: clickedItem })} />
            ) : null}
            <Button name="생성하기" onButtonClick={() => navigate("register")} />
        </Buttons>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 14px;
    min-width: 0;
    min-height: 0;
    height: 100%;
    padding: 20px 18px 16px;
    background: ${hw.surface};
    border: 1px solid ${hw.border};
    border-radius: 16px;
    box-shadow: ${hw.shadow};
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
    gap: 12px;
    padding-bottom: 12px;
    border-bottom: 1px solid ${hw.border};
    flex-shrink: 0;
`;

const Title = styled.h2`
    margin: 0;
    color: ${hw.text};
    font-size: 16px;
    font-weight: 700;
    letter-spacing: -0.02em;
`;

const Sub = styled.p`
    margin: 4px 0 0;
    font-size: 13px;
    color: ${hw.textMuted};
`;

const List = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 280px;
    border-radius: 12px;
    border: 1px solid ${hw.border};
    background: ${hw.surface};
    overflow-y: auto;
    overflow-x: hidden;
    ${scrollbar}
`;

const Item = styled.div<{ $on: boolean }>`
    padding: 12px 16px;
    border-bottom: 1px solid ${hw.border};
    color: ${hw.text};
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    background: ${props => props.$on ? hw.brandSoft : hw.surface};
    box-shadow: ${props => props.$on ? `inset 3px 0 0 ${hw.brand}` : "none"};

    &:hover {
        background: ${props => props.$on ? hw.brandSoft : hw.bg};
    }

    &:last-child {
        border-bottom: none;
    }
`;

const Empty = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 36px 16px;
    text-align: center;
`;

const EmptyTitle = styled.div`
    font-size: 14px;
    font-weight: 700;
    color: ${hw.text};
`;

const EmptyText = styled.div`
    font-size: 13px;
    color: ${hw.textMuted};
    line-height: 1.5;
`;

const Buttons = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    padding-top: 12px;
    border-top: 1px solid ${hw.border};
    flex-shrink: 0;
`;

export default LessonListView;
